import { headroomSourceLabel, headroomWindowLabel, percent } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * The account window, drawn from a headroom figure and where it came from.
 *
 * `unknown` is the whole reason this component exists rather than being three
 * lines inline. `headroom_source` is one of `transcript`, `configured`,
 * `unknown`, `limit_error` or `oauth`, and the column defaults to `unknown`
 * with `headroom` defaulting to `1` (see the check constraint in
 * `packages/db/src/schema/workers.ts`). So a device that has never reported
 * would otherwise render as a full green bar: a default presented as a
 * measurement. It gets the words "not reported yet" and no bar instead, in
 * every place headroom is drawn.
 *
 * `window` names which of the two vendor windows (`five_hour` or `seven_day`)
 * this headroom actually binds on (issue #22): oauth can report either one as
 * the tighter constraint, and a bar that always said "five-hour" was reporting
 * the weekly window's number under the wrong name. A person plans their
 * afternoon off one of these and their week off the other, so which one this
 * is matters more than the percentage itself.
 */
export function HeadroomBar({
  headroom,
  source,
  window,
  measuredAt,
  className,
}: {
  headroom: number;
  source?: string | null;
  window?: string | null;
  /** When the reading was taken, when that is older than the beat (issue #121). */
  measuredAt?: string | null;
  className?: string;
}) {
  if (!source || source === 'unknown') {
    return <p className={cn('text-fg-3 mt-2 text-xs', className)}>Window: not reported yet.</p>;
  }
  const tone = headroom > 0.5 ? 'bg-ok' : headroom > 0.2 ? 'bg-warn' : 'bg-bad';
  const label = headroomWindowLabel(window);
  return (
    <div className={cn('mt-2', className)}>
      <div className="bg-raised h-1.5 overflow-hidden rounded-full">
        <span
          className={cn('block h-full rounded-full transition-[width] duration-500', tone)}
          style={{ width: percent(headroom) }}
        />
      </div>
      <p className="text-fg-3 tnum mt-1.5 text-xs">
        {percent(headroom)} of the {label ? `${label} ` : ''}window left ·{' '}
        {headroomSourceLabel(source, measuredAt)}
      </p>
    </div>
  );
}
